/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
// utils
import qs from 'qs';
import { setSession } from '../utils/jwt';
import conObj, { conObjForm } from '../utils/connection-assistant';
import useLocales from '../hooks/useLocales';
import { getFingerprint, getLocalId } from '../utils/formatObject';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: !!localStorage.getItem(`accessToken`),
  isInitialized: false,
  user: localStorage.getItem(`userInfo`) ? JSON.parse(localStorage.getItem(`userInfo`)) : null,
  userProfile: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, userProfile } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      userProfile,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const { allLangs } = useLocales();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const accessToken = localStorage.getItem(`accessToken`);

      const search = window.location.search;
      const params = new URLSearchParams(search);
      const referralCode = params.get('reference');
      const lang = params.get('lang');

      if (referralCode) {
        localStorage.setItem(`referralCode`, referralCode);
      }

      if (lang) {
        const langExist = allLangs.find((_lang) => _lang.value === lang);
        if (langExist) {
          localStorage.setItem(`temp-lang`, langExist.value);
        }
      }
      console.log('accessToken', accessToken);
      if (accessToken) {
        setSession(accessToken);

        const response = await conObj.get('v1/user-profile');
        const { first_name, username, full_name, last_name, email } = response.data.data;
        const _user = { displayName: first_name, username, full_name, last_name, email };

        localStorage.setItem(`userInfo`, JSON.stringify(_user));

        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: _user,
            userProfile: response.data.data,
          },
        });
      } else {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            userProfile: null,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          userProfile: null,
        },
      });
    }
  };

  const login = async (username, password) => {
    localStorage.removeItem(`accessToken`);
    localStorage.removeItem(`userInfo`);

    const payload = {
      username,
      password,
    };

    console.log(payload);

    try {
      const { data: response } = await conObj.post('login', payload);

      const { token } = response;

      const _user = {};

      localStorage.setItem(`userInfo`, JSON.stringify(_user));

      if (token) {
        setSession(token);
        initialize();
        dispatch({
          type: 'LOGIN',
          payload: {
            user: _user,
          },
        });
      } else {
        throw new Error(response?.data?.msg);
      }
    } catch (error) {
      console.log('STATUS:', error.response?.status);
      console.log('DATA:', error.response?.data);
      console.log('FULL ERROR:', error);
      throw error;
    }
  };

  const register = async (
    currency_id,
    bank_id,
    username,
    password,
    full_name,
    contact_no,
    account_number,
    verification_code,
    referral_code
  ) => {
    localStorage.removeItem(`accessToken`);
    localStorage.removeItem(`userInfo`);

    console.log(verification_code);

    const payload = {
      'add_user[currency_id]': currency_id,
      'add_user[bank_id]': bank_id,
      'add_user[username]': username,
      'add_user[password]': password,
      'add_user[full_name]': full_name,
      'add_user[contact_no]': contact_no,
      'add_user[account_number]': account_number,
      'add_user[verification_code]': verification_code,
      'add_user[reference]': referral_code,
    };

    try {
      const { data: response } = await conObjForm.post('v1/register', payload);

      return response;
    } catch (error) {
      console.log('STATUS:', error.response?.status);
      console.log('DATA:', error.response?.data);
      console.log('FULL ERROR:', error);
      throw error;
    }
  };

  const requestOtp = async (phoneNumber, recaptchaToken) => {
    const payload = {
      'verify_phone_number[contact_no]': phoneNumber,
      'verify_phone_number[g-recaptcha-response]': recaptchaToken,
    };

    try {
      const { data: response } = await conObjForm.post('v1/request-otp', payload);

      return response;
    } catch (error) {
      console.log('STATUS:', error.response?.status);
      console.log('DATA:', error.response?.data);
      console.log('FULL ERROR:', error);
      throw error;
    }
  };

  const requestOtpResetPassword = async (phoneNumber, recaptchaToken) => {
    const payload = {
      'verify_forget_password[contact_no]': phoneNumber,
      'verify_forget_password[g-recaptcha-response]': recaptchaToken,
    };

    try {
      const { data: response } = await conObjForm.post('v1/forget-password', payload);

      return response;
    } catch (error) {
      console.log('STATUS:', error.response?.status);
      console.log('DATA:', error.response?.data);
      console.log('FULL ERROR:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await conObj.post('member_logout.json');
    } catch (error) {
      console.error(error);
    }
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        requestOtp,
        requestOtpResetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
